import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

export default function NotesFeed({ candidateId, jobId }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const fetchNotes = useCallback(() => {
    if (!candidateId) return;
    setLoading(true);
    const url = `${baseUrl}/job-posting/notes?candidateId=${candidateId}` +
                (jobId ? `&jobId=${jobId}` : "");
    axiosInstance
      .get(url)
      .then((r) => setNotes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [candidateId, jobId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const addNote = async () => {
    const text = body.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/notes`, {
        candidateId: Number(candidateId),
        jobId: jobId ? Number(jobId) : null,
        body: text,
      });
      setBody("");
      fetchNotes();
    } catch (e) {
      // surface error inline
      console.error("Failed to add note", e);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-vf-base">Notes & activity</h3>
        <span className="text-xs text-vf-muted">{notes.length}</span>
      </div>

      <div className="mb-4">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a note about this candidate…"
          className="w-full px-3 py-2 border border-slate-200 rounded text-sm resize-none"
          rows={2}
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={addNote}
            disabled={!body.trim() || posting}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs disabled:opacity-50"
          >
            {posting ? "Adding…" : "Add note"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-vf-muted py-3">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="text-xs text-vf-muted py-3 text-center">
          No notes yet. Add the first one.
        </div>
      ) : (
        <ul className="space-y-3">
          {notes.map((n) => (
            <li key={n.id} className="border-l-2 border-vf-primary pl-3 py-1">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-medium text-vf-base">User #{n.authorId}</span>
                <span className="text-[10px] text-vf-muted">{fmtDate(n.createdAt)}</span>
              </div>
              <p className="text-sm text-vf-base whitespace-pre-wrap mt-0.5">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
